"use client";

import { useEffect, useState } from "react";
import { useSessionStatus } from "~/hooks/useSessionStatus";
import { formatTimeRemaining } from "~/lib/auth-utils";

export function SessionMonitor({
  showWarning = true,
  warningThresholdMinutes = 5,
  checkIntervalSeconds = 60,
  position = "top-right",
}: {
  showWarning?: boolean;
  warningThresholdMinutes?: number;
  checkIntervalSeconds?: number;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}) {
  const sessionStatus = useSessionStatus({
    warningThresholdMinutes,
    checkIntervalSeconds,
    autoLogoutOnExpiry: true,
  });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!sessionStatus.isExpiringSoon) {
      setDismissed(false);
    }
  }, [sessionStatus.isExpiringSoon]);

  if (!showWarning || dismissed || sessionStatus.isLoading) {
    return null;
  }

  if (!sessionStatus.isExpiringSoon || sessionStatus.isExpired) {
    return null;
  }

  const positionClasses = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4",
  };

  return (
    <div
      className={`fixed ${positionClasses[position]} z-50 max-w-sm rounded-lg border border-yellow-400 bg-yellow-50 p-4 shadow-lg`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          <svg
            className="h-6 w-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-semibold text-yellow-800">
            Session Expiring Soon
          </h3>
          <p className="mt-1 text-sm text-yellow-700">
            Your session will expire in{" "}
            <span className="font-semibold">
              {formatTimeRemaining(sessionStatus.timeRemaining)}
            </span>
            . Please save your work.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded-md text-yellow-600 hover:text-yellow-800 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          aria-label="Dismiss warning"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

